import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

const RequirementsSelector = ({
  selectedRequirements = [],
  liftType,
  buildingType,
  floors,
  onChange,
  onContinue,
  onBack,
}) => {
  const defaultRequirements = {
    capacity: 544,
    speed: 1.0,
    safety: ["emergencyPower", "overload"], // Set Emergency Power and Overload Detection as included by default
    accessibility: [],
  };

  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  const [requirements, setRequirements] = useState(() => {
    const initialReqs = { ...defaultRequirements };

    // Clear both safety and accessibility arrays
    initialReqs.safety = [];
    initialReqs.accessibility = [];

    // Process all requirements from props
    selectedRequirements.forEach((req) => {
      // Extract capacity value (format: "capacity_630")
      if (req.startsWith("capacity_")) {
        const capacityValue = parseInt(req.split("_")[1], 10);
        if (!isNaN(capacityValue)) {
          initialReqs.capacity = capacityValue;
        }
      }
      // Extract speed value (format: "speed_1.0")
      else if (req.startsWith("speed_")) {
        const speedValue = parseFloat(req.split("_")[1]);
        if (!isNaN(speedValue)) {
          initialReqs.speed = speedValue;
        }
      }
      // Safety features - use a more comprehensive check
      else if (
        req.startsWith("emergency") ||
        req.startsWith("fire") ||
        req.includes("overload") ||
        req.includes("security") ||
        req === "securityCamera"
      ) {
        initialReqs.safety.push(req);
      }
      // Accessibility features - expand the check to include all features
      else if (
        req.includes("braille") ||
        req.includes("voice") ||
        req.includes("handrail") ||
        req.includes("Mirror") ||
        req.includes("Buttons") ||
        req === "visibilityMirror" ||
        req === "loweredButtons" ||
        req === "voiceAnnouncements"
      ) {
        initialReqs.accessibility.push(req);
      }
    });

    // Only use defaults if nothing was selected
    if (initialReqs.safety.length === 0) {
      initialReqs.safety = [...defaultRequirements.safety];
    }

    return initialReqs;
  });

  const [activeTab, setActiveTab] = useState("capacity");

  // Add window size tracking for responsive design
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Define responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth <= 1024 && windowWidth > 768;
  const isSmallMobile = windowWidth <= 480;

  // Update parent component when requirements change
  useEffect(() => {
    if (onChange) {
      // Format current requirements for comparison
      const allFeatures = [
        `capacity_${requirements.capacity}`,
        `speed_${requirements.speed}`,
        ...requirements.safety,
        ...requirements.accessibility,
      ];

      // Only call onChange if the requirements are different from what was passed in
      if (!arraysEqual(allFeatures, selectedRequirements)) {
        onChange(allFeatures);
      }
    }
  }, [
    requirements.capacity,
    requirements.speed,
    requirements.safety,
    requirements.accessibility,
    onChange,
    selectedRequirements, // Add this to the dependency array
  ]);

  // Helper to get lift type name
  const getLiftTypeName = () => {
    const liftNames = {
      mrl: "Machine Room-Less (MRL)",
      hydraulic: "Hydraulic",
      dumbwaiter: "Dumbwaiter",
      "car-lift": "Car Lift",
    };

    return liftNames[liftType] || "Custom Elevator";
  };

  // Get appropriate icon for lift type
  const getLiftTypeIcon = () => {
    const icons = {
      mrl: "elevator",
      hydraulic: "tachometer-alt",
      dumbwaiter: "utensils",
      "car-lift": "car",
    };

    return icons[liftType] || "elevator";
  };

  // Handle checkbox selection for arrays like safety features
  const handleCheckboxChange = (category, item) => {
    setRequirements((prev) => {
      const currentItems = [...prev[category]];
      if (currentItems.includes(item)) {
        return {
          ...prev,
          [category]: currentItems.filter((i) => i !== item),
        };
      } else {
        return { ...prev, [category]: [...currentItems, item] };
      }
    });
  };

  // Handle single value changes
  const handleValueChange = (field, value) => {
    setRequirements((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Format large numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Get responsive styles based on screen width
  const getResponsiveStyles = () => {
    return {
      container: {
        padding: isSmallMobile ? "15px 10px" : isMobile ? "20px 15px" : "20px",
      },
      title: {
        fontSize: isSmallMobile ? "22px" : isMobile ? "24px" : "28px",
        marginBottom: isSmallMobile ? "4px" : "6px",
      },
      subtitle: {
        fontSize: isSmallMobile ? "14px" : "16px",
      },
      tabNav: {
        flexDirection: isSmallMobile ? "column" : "row",
      },
      tabButton: {
        padding: isSmallMobile ? "12px 8px" : "14px 10px",
        fontSize: isSmallMobile ? "13px" : "14px",
      },
      panel: {
        padding: isSmallMobile ? "15px" : isMobile ? "20px" : "25px",
      },
      panelTitle: {
        fontSize: isSmallMobile ? "18px" : isMobile ? "19px" : "20px",
      },
      panelDescription: {
        fontSize: isSmallMobile ? "14px" : "15px",
      },
      optionsGrid: {
        gridTemplateColumns: isMobile
          ? "1fr"
          : "repeat(auto-fit, minmax(300px, 1fr))",
        gap: isSmallMobile ? "20px" : "30px",
      },
      capacityOptions: {
        gridTemplateColumns: isSmallMobile
          ? "repeat(2, 1fr)"
          : isMobile
          ? "repeat(3, 1fr)"
          : "repeat(auto-fill, minmax(110px, 1fr))",
      },
      capacityOption: {
        padding: isSmallMobile ? "12px 8px" : "15px 10px",
      },
      capacityValue: {
        fontSize: isSmallMobile ? "18px" : "22px",
      },
      capacityUnit: {
        fontSize: isSmallMobile ? "12px" : "14px",
      },
      currentSpeed: {
        fontSize: isSmallMobile ? "26px" : "32px",
      },
      featuresGrid: {
        gridTemplateColumns: isMobile
          ? "1fr"
          : "repeat(auto-fit, minmax(300px, 1fr))",
        gap: isSmallMobile ? "20px" : "30px",
      },
      checkboxItem: {
        padding: isSmallMobile ? "8px 10px" : "10px 12px",
      },
      summaryPanel: {
        padding: isSmallMobile ? "15px" : "20px",
      },
      summaryGrid: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(180px, 1fr))",
      },
      navigation: {
        flexDirection: isSmallMobile ? "column" : "row",
        gap: isSmallMobile ? "12px" : "0",
      },
      backButton: {
        width: isSmallMobile ? "100%" : "auto",
        justifyContent: isSmallMobile ? "center" : "flex-start",
        padding: isSmallMobile ? "10px 15px" : "12px 24px",
      },
      nextButton: {
        width: isSmallMobile ? "100%" : "auto",
        justifyContent: isSmallMobile ? "center" : "flex-start",
        padding: isSmallMobile ? "10px 15px" : "12px 24px",
      },
      recommendationBox: {
        flexDirection: isSmallMobile ? "column" : "row",
        alignItems: isSmallMobile ? "flex-start" : "center",
        padding: isSmallMobile ? "12px" : "15px",
      },
      recommendationIcon: {
        marginRight: isSmallMobile ? "0" : "15px",
        marginBottom: isSmallMobile ? "10px" : "0",
      },
      speedSlider: {
        height: isSmallMobile ? "10px" : "6px", // Larger touch target on mobile
      },
      checkboxLabel: {
        fontSize: isSmallMobile ? "13px" : "14px",
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();

  return (
    <div style={{ ...styles.container, ...responsiveStyles.container }}>
      <div style={styles.header}>
        <h2 style={{ ...styles.title, ...responsiveStyles.title }}>
          <i
            className={`fas fa-${getLiftTypeIcon()}`}
            style={styles.titleIcon}
          ></i>
          Configure Elevator Requirements
        </h2>
        <p style={{ ...styles.subtitle, ...responsiveStyles.subtitle }}>
          {getLiftTypeName()} • {buildingType} building • {floors} floors
        </p>
      </div>

      {/* Tab navigation */}
      <div style={{ ...styles.tabNav, ...responsiveStyles.tabNav }}>
        <button
          style={{
            ...styles.tabButton,
            ...responsiveStyles.tabButton,
            ...(activeTab === "capacity" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab("capacity")}
        >
          <i className="fas fa-weight-hanging"></i>
          <span style={styles.tabText}>Capacity & Speed</span>
        </button>
        <button
          style={{
            ...styles.tabButton,
            ...responsiveStyles.tabButton,
            ...(activeTab === "features" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab("features")}
        >
          <i className="fas fa-shield-alt"></i>
          <span style={styles.tabText}>Features</span>
        </button>
      </div>

      {/* Tab content */}
      <motion.div
        style={styles.tabContent}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Capacity and Speed tab */}
        {activeTab === "capacity" && (
          <div style={{ ...styles.panel, ...responsiveStyles.panel }}>
            <div style={styles.panelHeader}>
              <h3
                style={{ ...styles.panelTitle, ...responsiveStyles.panelTitle }}
              >
                Capacity & Speed
              </h3>
              <p
                style={{
                  ...styles.panelDescription,
                  ...responsiveStyles.panelDescription,
                }}
              >
                Select the weight capacity and speed for your elevator
              </p>
            </div>

            <div
              style={{ ...styles.optionsGrid, ...responsiveStyles.optionsGrid }}
            >
              {/* Capacity options */}
              <div style={styles.option}>
                <h4 style={styles.optionTitle}>
                  <i
                    className="fas fa-weight-hanging"
                    style={styles.optionIcon}
                  ></i>
                  Weight Capacity
                </h4>

                <div
                  style={{
                    ...styles.capacityOptions,
                    ...responsiveStyles.capacityOptions,
                  }}
                >
                  {[340, 408, 544, 680, 884, 1360].map((cap) => (
                    <div
                      key={cap}
                      style={{
                        ...styles.capacityOption,
                        ...responsiveStyles.capacityOption,
                        ...(requirements.capacity === cap
                          ? styles.selectedCapacity
                          : {}),
                      }}
                      onClick={() => handleValueChange("capacity", cap)}
                    >
                      <div
                        style={{
                          ...styles.capacityValue,
                          ...responsiveStyles.capacityValue,
                        }}
                      >
                        {cap}
                      </div>
                      <div
                        style={{
                          ...styles.capacityUnit,
                          ...responsiveStyles.capacityUnit,
                        }}
                      >
                        kg
                      </div>
                      <div style={styles.capacityDesc}>
                        {cap <= 450
                          ? "4-5 persons"
                          : cap <= 630
                          ? "8 persons"
                          : cap <= 800
                          ? "10 persons"
                          : cap <= 1000
                          ? "13 persons"
                          : "20+ persons"}
                      </div>
                      {requirements.capacity === cap && (
                        <i
                          className="fas fa-check-circle"
                          style={styles.optionCheck}
                        ></i>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Speed selector */}
              <div style={styles.option}>
                <h4 style={styles.optionTitle}>
                  <i
                    className="fas fa-tachometer-alt"
                    style={styles.optionIcon}
                  ></i>
                  Travel Speed
                </h4>

                <div style={styles.speedSection}>
                  <div style={styles.speedSliderContainer}>
                    <div style={styles.speedValue}>
                      <span
                        style={{
                          ...styles.currentSpeed,
                          ...responsiveStyles.currentSpeed,
                        }}
                      >
                        {requirements.speed.toFixed(1)}
                      </span>
                      <span style={styles.speedUnit}>m/s</span>
                    </div>

                    <input
                      type="range"
                      min="1"
                      max="2"
                      step="0.1"
                      value={requirements.speed}
                      onChange={(e) =>
                        handleValueChange("speed", parseFloat(e.target.value))
                      }
                      style={{
                        ...styles.speedSlider,
                        ...responsiveStyles.speedSlider,
                      }}
                    />

                    <div style={styles.speedLabels}>
                      <span>1 m/s</span>
                      <span>2 m/s</span>
                    </div>
                  </div>

                  <div style={styles.speedInfo}>
                    {requirements.speed <= 0.7 && (
                      <div style={styles.speedInfoText}>
                        <i className="fas fa-info-circle"></i>
                        Economical option for low-rise buildings (2-3 floors)
                      </div>
                    )}
                    {requirements.speed > 0.7 && requirements.speed <= 1.2 && (
                      <div style={styles.speedInfoText}>
                        <i className="fas fa-info-circle"></i>
                        Standard speed for mid-rise buildings (4-8 floors)
                      </div>
                    )}
                    {requirements.speed > 1.2 && requirements.speed <= 1.75 && (
                      <div style={styles.speedInfoText}>
                        <i className="fas fa-info-circle"></i>
                        High-speed option for taller buildings (9-15 floors)
                      </div>
                    )}
                    {requirements.speed > 1.75 && (
                      <div style={styles.speedInfoText}>
                        <i className="fas fa-info-circle"></i>
                        Premium speed for high-rise buildings (15+ floors)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.recommendationBox,
                ...responsiveStyles.recommendationBox,
              }}
            >
              <div
                style={{
                  ...styles.recommendationIcon,
                  ...responsiveStyles.recommendationIcon,
                }}
              >
                <i className="fas fa-lightbulb"></i>
              </div>
              <div style={styles.recommendationContent}>
                <h4 style={styles.recommendationTitle}>Recommendation</h4>
                <p style={styles.recommendationText}>
                  For a {buildingType} building with {floors} floors, we
                  recommend a capacity of{" "}
                  {floors <= 4 ? "630" : floors <= 10 ? "800" : "1000"}kg with a
                  speed of {floors <= 4 ? "1.0" : floors <= 10 ? "1.6" : "2.0"}
                  m/s for optimal performance.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Features tab */}
        {activeTab === "features" && (
          <div style={{ ...styles.panel, ...responsiveStyles.panel }}>
            <div style={styles.panelHeader}>
              <h3
                style={{ ...styles.panelTitle, ...responsiveStyles.panelTitle }}
              >
                Safety & Accessibility
              </h3>
              <p
                style={{
                  ...styles.panelDescription,
                  ...responsiveStyles.panelDescription,
                }}
              >
                Select safety features and accessibility options
              </p>
            </div>

            <div
              style={{
                ...styles.featuresGrid,
                ...responsiveStyles.featuresGrid,
              }}
            >
              {/* Safety features */}
              <div style={styles.featureSection}>
                <h4 style={styles.featureSectionTitle}>
                  <i
                    className="fas fa-shield-alt"
                    style={styles.featureIcon}
                  ></i>
                  Safety Features
                </h4>

                <div style={styles.checkboxList}>
                  <label
                    style={{
                      ...styles.checkboxItem,
                      ...responsiveStyles.checkboxItem,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={requirements.safety.includes("emergencyPower")}
                      onChange={() =>
                        handleCheckboxChange("safety", "emergencyPower")
                      }
                      style={styles.checkbox}
                    />
                    <div style={styles.checkboxContent}>
                      <div
                        style={{
                          ...styles.checkboxLabel,
                          ...responsiveStyles.checkboxLabel,
                        }}
                      >
                        Emergency Power Operation
                      </div>
                      <div style={styles.checkboxDesc}>
                        Elevator continues to operate during power outages
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      ...styles.checkboxItem,
                      ...responsiveStyles.checkboxItem,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={requirements.safety.includes("overload")}
                      onChange={() =>
                        handleCheckboxChange("safety", "overload")
                      }
                      style={styles.checkbox}
                    />
                    <div style={styles.checkboxContent}>
                      <div
                        style={{
                          ...styles.checkboxLabel,
                          ...responsiveStyles.checkboxLabel,
                        }}
                      >
                        Overload Detection System
                      </div>
                      <div style={styles.checkboxDesc}>
                        Prevents operation when elevator exceeds capacity
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      ...styles.checkboxItem,
                      ...responsiveStyles.checkboxItem,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={requirements.safety.includes("fireAlarm")}
                      onChange={() =>
                        handleCheckboxChange("safety", "fireAlarm")
                      }
                      style={styles.checkbox}
                    />
                    <div style={styles.checkboxContent}>
                      <div
                        style={{
                          ...styles.checkboxLabel,
                          ...responsiveStyles.checkboxLabel,
                        }}
                      >
                        Fire Alarm Integration
                      </div>
                      <div style={styles.checkboxDesc}>
                        Automatically returns elevator to main floor during fire
                        emergencies
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      ...styles.checkboxItem,
                      ...responsiveStyles.checkboxItem,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={requirements.safety.includes("emergencyCall")}
                      onChange={() =>
                        handleCheckboxChange("safety", "emergencyCall")
                      }
                      style={styles.checkbox}
                    />
                    <div style={styles.checkboxContent}>
                      <div
                        style={{
                          ...styles.checkboxLabel,
                          ...responsiveStyles.checkboxLabel,
                        }}
                      >
                        Emergency Call System
                      </div>
                      <div style={styles.checkboxDesc}>
                        Direct communication line to building management or
                        emergency services
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      ...styles.checkboxItem,
                      ...responsiveStyles.checkboxItem,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={requirements.safety.includes("securityCamera")}
                      onChange={() =>
                        handleCheckboxChange("safety", "securityCamera")
                      }
                      style={styles.checkbox}
                    />
                    <div style={styles.checkboxContent}>
                      <div
                        style={{
                          ...styles.checkboxLabel,
                          ...responsiveStyles.checkboxLabel,
                        }}
                      >
                        Security Camera
                      </div>
                      <div style={styles.checkboxDesc}>
                        In-cab camera monitoring for enhanced security and
                        passenger safety
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Accessibility features */}
              <div style={styles.checkboxList}>
                <label
                  style={{
                    ...styles.checkboxItem,
                    ...responsiveStyles.checkboxItem,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={requirements.accessibility.includes("braille")}
                    onChange={() =>
                      handleCheckboxChange("accessibility", "braille")
                    }
                    style={styles.checkbox}
                  />
                  <div style={styles.checkboxContent}>
                    <div
                      style={{
                        ...styles.checkboxLabel,
                        ...responsiveStyles.checkboxLabel,
                      }}
                    >
                      Braille Button Markings
                    </div>
                    <div style={styles.checkboxDesc}>
                      Tactile indicators on control panel
                    </div>
                  </div>
                </label>

                {/* Add Voice Announcements */}
                <label
                  style={{
                    ...styles.checkboxItem,
                    ...responsiveStyles.checkboxItem,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={requirements.accessibility.includes(
                      "voiceAnnouncements"
                    )}
                    onChange={() =>
                      handleCheckboxChange(
                        "accessibility",
                        "voiceAnnouncements"
                      )
                    }
                    style={styles.checkbox}
                  />
                  <div style={styles.checkboxContent}>
                    <div
                      style={{
                        ...styles.checkboxLabel,
                        ...responsiveStyles.checkboxLabel,
                      }}
                    >
                      Voice Announcements
                    </div>
                    <div style={styles.checkboxDesc}>
                      Audio announcements of floor levels and elevator status
                    </div>
                  </div>
                </label>

                {/* Add Visibility Mirror */}
                <label
                  style={{
                    ...styles.checkboxItem,
                    ...responsiveStyles.checkboxItem,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={requirements.accessibility.includes(
                      "visibilityMirror"
                    )}
                    onChange={() =>
                      handleCheckboxChange("accessibility", "visibilityMirror")
                    }
                    style={styles.checkbox}
                  />
                  <div style={styles.checkboxContent}>
                    <div
                      style={{
                        ...styles.checkboxLabel,
                        ...responsiveStyles.checkboxLabel,
                      }}
                    >
                      Visibility Mirror
                    </div>
                    <div style={styles.checkboxDesc}>
                      Angled mirror to assist wheelchair users with maneuvering
                    </div>
                  </div>
                </label>

                {/* Add Lowered Buttons */}
                <label
                  style={{
                    ...styles.checkboxItem,
                    ...responsiveStyles.checkboxItem,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={requirements.accessibility.includes(
                      "loweredButtons"
                    )}
                    onChange={() =>
                      handleCheckboxChange("accessibility", "loweredButtons")
                    }
                    style={styles.checkbox}
                  />
                  <div style={styles.checkboxContent}>
                    <div
                      style={{
                        ...styles.checkboxLabel,
                        ...responsiveStyles.checkboxLabel,
                      }}
                    >
                      Lowered Buttons
                    </div>
                    <div style={styles.checkboxDesc}>
                      Control panel positioned for accessibility from seated
                      height
                    </div>
                  </div>
                </label>

                {/* Add Handrails */}
                <label
                  style={{
                    ...styles.checkboxItem,
                    ...responsiveStyles.checkboxItem,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={requirements.accessibility.includes("handrails")}
                    onChange={() =>
                      handleCheckboxChange("accessibility", "handrails")
                    }
                    style={styles.checkbox}
                  />
                  <div style={styles.checkboxContent}>
                    <div
                      style={{
                        ...styles.checkboxLabel,
                        ...responsiveStyles.checkboxLabel,
                      }}
                    >
                      Handrails
                    </div>
                    <div style={styles.checkboxDesc}>
                      Ergonomic support rails at ADA-compliant height
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div style={styles.regulationNote}>
              <i className="fas fa-info-circle" style={styles.noteIcon}></i>
              <p style={styles.noteText}>
                Some features may be required by local building codes and
                accessibility regulations. We'll guide you through the required
                features for your specific building during the consultation.
              </p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Summary panel - shows on all tabs */}
      <div style={{ ...styles.summaryPanel, ...responsiveStyles.summaryPanel }}>
        <h4 style={styles.summaryTitle}>Your Selected Requirements</h4>
        <div style={{ ...styles.summaryGrid, ...responsiveStyles.summaryGrid }}>
          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Capacity</div>
            <div style={styles.summaryValue}>
              {formatNumber(requirements.capacity)} kg
            </div>
          </div>

          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Speed</div>
            <div style={styles.summaryValue}>
              {requirements.speed.toFixed(1)} m/s
            </div>
          </div>

          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Safety</div>
            <div style={styles.summaryValue}>
              {requirements.safety.length} features selected
            </div>
          </div>

          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Accessibility</div>
            <div style={styles.summaryValue}>
              {requirements.accessibility.length} features selected
            </div>
          </div>
        </div>
      </div>

      {/* Navigation buttons */}
      <div style={{ ...styles.navigation, ...responsiveStyles.navigation }}>
        <button
          onClick={onBack}
          style={{ ...styles.backButton, ...responsiveStyles.backButton }}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>

        <button
          onClick={onContinue}
          style={{ ...styles.nextButton, ...responsiveStyles.nextButton }}
        >
          Continue <i className="fas fa-arrow-right"></i>
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "20px",
    minWidth: "320px",
    overflow: "hidden",
    fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    margin: "0 0 6px",
    color: "#1d1d1f",
  },
  titleIcon: {
    marginRight: "8px",
    color: "#007aff",
  },
  subtitle: {
    fontSize: "16px",
    color: "#4d4d4d",
    margin: 0,
  },
  tabNav: {
    display: "flex",
    borderRadius: "12px",
    overflow: "hidden",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "#f5f5f7",
  },
  tabButton: {
    flex: 1,
    padding: "14px 10px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#4d4d4d",
    transition: "all 0.2s ease",
  },
  tabText: {
    display: "block",
    "@media (max-width: 480px)": {
      fontSize: "12px",
    },
  },
  activeTab: {
    backgroundColor: "#ffffff",
    color: "#007aff",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  tabContent: {
    marginBottom: "30px",
  },
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: "15px",
    padding: "25px",
    boxShadow: "0 5px 20px rgba(0,0,0,0.06)",
  },
  panelHeader: {
    marginBottom: "25px",
  },
  panelTitle: {
    fontSize: "20px",
    fontWeight: "600",
    margin: "0 0 8px",
    color: "#1d1d1f",
  },
  panelDescription: {
    fontSize: "15px",
    color: "#666",
    margin: 0,
  },
  optionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "30px",
    marginBottom: "20px",
  },
  option: {
    display: "flex",
    flexDirection: "column",
  },
  optionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    margin: "0 0 15px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#333",
  },
  optionIcon: {
    color: "#007aff",
  },
  capacityOptions: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
    gap: "10px",
  },
  capacityOption: {
    border: "1px solid #e0e0e0",
    borderRadius: "10px",
    padding: "15px 10px",
    textAlign: "center",
    cursor: "pointer",
    position: "relative",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
    "&:hover": {
      borderColor: "#007aff",
      boxShadow: "0 4px 12px rgba(0,122,255,0.1)",
    },
  },
  selectedCapacity: {
    borderColor: "#007aff",
    backgroundColor: "rgba(0,122,255,0.05)",
    boxShadow: "0 4px 12px rgba(0,122,255,0.1)",
  },
  capacityValue: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#333",
  },
  capacityUnit: {
    fontSize: "14px",
    color: "#777",
    marginTop: "2px",
  },
  capacityDesc: {
    fontSize: "12px",
    color: "#666",
    marginTop: "6px",
  },
  optionCheck: {
    position: "absolute",
    top: "8px",
    right: "8px",
    color: "#007aff",
    fontSize: "16px",
  },
  speedSection: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  speedSliderContainer: {
    padding: "10px 0",
  },
  speedValue: {
    display: "flex",
    alignItems: "baseline",
    marginBottom: "15px",
    justifyContent: "center",
  },
  currentSpeed: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#007aff",
  },
  speedUnit: {
    fontSize: "16px",
    color: "#666",
    marginLeft: "5px",
  },
  speedSlider: {
    width: "100%",
    height: "6px",
    appearance: "none",
    backgroundColor: "#e0e0e0",
    borderRadius: "3px",
    outline: "none",
    "&::-webkit-slider-thumb": {
      appearance: "none",
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      background: "#007aff",
      cursor: "pointer",
      boxShadow: "0 1px 5px rgba(0,0,0,0.15)",
    },
  },
  speedLabels: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "8px",
    color: "#777",
    fontSize: "13px",
  },
  speedInfo: {
    backgroundColor: "#f5f9ff",
    padding: "12px 15px",
    borderRadius: "10px",
    border: "1px solid #e6eeff",
  },
  speedInfoText: {
    fontSize: "14px",
    color: "#0055b3",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  recommendationBox: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#fffbf0",
    borderRadius: "12px",
    padding: "15px",
    marginTop: "30px",
    border: "1px solid #f7e9bc",
  },
  recommendationIcon: {
    color: "#f5a623",
    fontSize: "24px",
    marginRight: "15px",
  },
  recommendationContent: {
    flex: 1,
  },
  recommendationTitle: {
    margin: "0 0 5px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
  },
  recommendationText: {
    margin: 0,
    fontSize: "14px",
    color: "#555",
    lineHeight: "1.4",
  },
  finishOptions: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "10px",
  },
  finishOption: {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #e0e0e0",
    transition: "all 0.2s ease",
    cursor: "pointer",
    position: "relative",
    "&:hover": {
      borderColor: "#007aff",
      boxShadow: "0 4px 12px rgba(0,122,255,0.1)",
    },
  },
  selectedFinish: {
    borderColor: "#007aff",
    backgroundColor: "rgba(0,122,255,0.05)",
  },
  finishSwatch: {
    width: "36px",
    height: "36px",
    borderRadius: "6px",
    marginRight: "12px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  finishStainless: {
    background: "linear-gradient(135deg, #c0c0c0, #e8e8e8, #c0c0c0)",
  },
  finishPainted: {
    background: "linear-gradient(135deg, #4a90e2, #5da0f2)",
  },
  finishGlass: {
    background: "linear-gradient(135deg, #a8d8fd, #d4ebff)",
    border: "1px solid #e0e0e0",
  },
  finishBronze: {
    background: "linear-gradient(135deg, #b08d57, #d4b483)",
  },
  finishInfo: {
    flex: 1,
  },
  finishName: {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "2px",
  },
  finishDesc: {
    fontSize: "12px",
    color: "#666",
  },
  finishCheck: {
    position: "absolute",
    top: "10px",
    right: "10px",
    color: "#007aff",
  },
  featuresGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "30px",
  },
  featureSection: {
    marginBottom: "20px",
  },
  featureSectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "15px",
    color: "#333",
  },
  featureIcon: {
    color: "#007aff",
  },
  checkboxList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  checkboxItem: {
    display: "flex",
    alignItems: "flex-start",
    padding: "10px 12px",
    borderRadius: "8px",
    backgroundColor: "#f9f9fb",
    transition: "background-color 0.2s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f0f0f5",
    },
  },
  checkbox: {
    marginRight: "12px",
    marginTop: "3px",
    width: "16px",
    height: "16px",
    accentColor: "#007aff",
  },
  checkboxContent: {
    flex: 1,
  },
  checkboxLabel: {
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "2px",
  },
  checkboxDesc: {
    fontSize: "12px",
    color: "#666",
  },
  regulationNote: {
    display: "flex",
    alignItems: "flex-start",
    padding: "15px",
    backgroundColor: "#f0f7ff",
    borderRadius: "10px",
    marginTop: "20px",
  },
  noteIcon: {
    color: "#0055b3",
    fontSize: "18px",
    marginRight: "10px",
    marginTop: "2px",
  },
  noteText: {
    margin: 0,
    fontSize: "14px",
    color: "#0055b3",
    flex: 1,
    lineHeight: "1.4",
  },
  summaryPanel: {
    backgroundColor: "#f9f9fb",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "30px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    border: "1px solid #eaeaec",
  },
  summaryTitle: {
    margin: "0 0 15px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "15px",
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  summaryLabel: {
    fontSize: "13px",
    color: "#666",
  },
  summaryValue: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#333",
  },
  navigation: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "30px",
  },
  backButton: {
    padding: "12px 24px",
    borderRadius: "8px",
    border: "1px solid #d0d0d0",
    backgroundColor: "white",
    color: "#4d4d4d",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f7",
    },
  },
  nextButton: {
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#007aff",
    color: "white",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 4px 12px rgba(0,122,255,0.2)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#0062cc",
    },
  },
  // Responsive adjustments
  "@media (max-width: 768px)": {
    optionsGrid: {
      gridTemplateColumns: "1fr",
      gap: "20px",
    },
    featuresGrid: {
      gridTemplateColumns: "1fr",
      gap: "20px",
    },
    panelTitle: {
      fontSize: "18px",
    },
    summaryGrid: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  "@media (max-width: 480px)": {
    panel: {
      padding: "15px",
    },
    capacityOptions: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    finishOptions: {
      gridTemplateColumns: "1fr",
    },
    summaryGrid: {
      gridTemplateColumns: "1fr",
    },
    navigation: {
      flexDirection: "column",
      gap: "15px",
    },
    backButton: {
      width: "100%",
      justifyContent: "center",
    },
    nextButton: {
      width: "100%",
      justifyContent: "center",
    },
  },
};

export default RequirementsSelector;
